import restClient from "api/restClient";
import { isRequestCancelled } from "api/utils";
import NotificationService from "components/Notification/Notification.service";
import { call, put, takeLatest } from "redux-saga/effects";
import { actions as authActions } from "./Auth.reducer";
import { loginCheckUrl, logoutPath } from "./constants";

export function* signIn({
    username,
    password,
    twofaCode,
    authenticationMethod,
    promise,
    preventRedirectToWorkspace,
}) {
    try {
        const response = yield restClient.post(`/auth/login`, {
            user_name: username,
            password: password,
            twofa_code: twofaCode,
            authentication_method: authenticationMethod,
        });

        if (response?.data?.is_engine_user === false) {
            yield call(promise.reject, {
                is_engine_user:
                    "Your user is not authorised to use the Cloudomation Engine UI. Please talk to your Cloudomation administrator if you need access to the Cloudomation Engine UI.",
            });

            yield put({
                type: authActions.SIGN_IN_FAILED,
            });
            return;
        }

        if (preventRedirectToWorkspace) {
            yield put({
                type: authActions.REDIRECT_TO_WORKSPACE,
                response: false,
            });
        } else {
            yield put({
                type: authActions.SIGN_IN_SUCCESSFUL,
            });
        }

        // Ensure backend version is available immediately after login (topbar tooltip)
        // even before websocket/workspace config has fully initialized.
        try {
            const checkResp = yield restClient.get(loginCheckUrl);
            if (checkResp?.data?.backend_version != null) {
                yield put({
                    type: authActions.STORE_BACKEND_VERSION,
                    response: checkResp.data.backend_version,
                });
            }
            const publicInfoUpdate = {};
            if (checkResp?.data?.workspace_name != null) {
                publicInfoUpdate.workspaceName = checkResp.data.workspace_name;
            }
            // Only overwrite when present, so a missing value keeps the last
            // known one (e.g. from the unauthenticated 401) and avoids a topbar
            // color flash.
            if (checkResp?.data?.workspace_highlight_color != null) {
                publicInfoUpdate.workspaceHighlightColor = checkResp.data.workspace_highlight_color;
            }
            if (checkResp?.data?.backend_version != null) {
                publicInfoUpdate.backendVersion = checkResp.data.backend_version;
            }
            if (Object.keys(publicInfoUpdate).length > 0) {
                yield put({
                    type: authActions.STORE_AUTH_CHECK_PUBLIC_INFO,
                    response: publicInfoUpdate,
                });
            }
        } catch (_err) {
            // ignore
        }

        yield call(promise.resolve, response?.data);
    } catch (error) {
        if (isRequestCancelled(error)) {
            return;
        }
        yield put({
            type: authActions.SIGN_IN_FAILED,
        });

        yield call(promise.reject, error);
    }
}

/**
 * Sign Out Saga
 */
export function* signOut({ promise }) {
    try {
        try {
            yield restClient.get(logoutPath);
        } finally {
            // Always clear local session state, even if the logout request fails or the
            // backend is unreachable — otherwise the UI stays stuck as "signed in" and the
            // 401 interceptor re-fires logout on every subsequent request.
            yield put({ type: authActions.SIGN_OUT_SUCCESSFUL });
        }

        // Refresh public workspace info (name, highlight, BE version) for the login screen
        // without requiring a full page refresh.
        try {
            const resp = yield restClient.get(loginCheckUrl);
            yield put({
                type: authActions.STORE_AUTH_CHECK_PUBLIC_INFO,
                response: {
                    workspaceName: resp?.data?.workspace_name ?? null,
                    workspaceHighlightColor: resp?.data?.workspace_highlight_color ?? null,
                    backendVersion: resp?.data?.backend_version ?? null,
                },
            });
        } catch (err) {
            if (isRequestCancelled(err)) {
                if (promise) yield call(promise?.resolve);
                return;
            }
            const data = err?.response?.data;
            if (data) {
                yield put({
                    type: authActions.STORE_AUTH_CHECK_PUBLIC_INFO,
                    response: {
                        workspaceName: data?.workspace_name ?? null,
                        workspaceHighlightColor: data?.workspace_highlight_color ?? null,
                        backendVersion: data?.backend_version ?? null,
                    },
                });
            }
        }

        if (promise) yield call(promise?.resolve);
    } catch (error) {
        if (isRequestCancelled(error)) {
            if (promise) yield call(promise?.resolve);
            return;
        }
        if (promise) yield call(promise?.reject, error);
        NotificationService.error(error?.response?.data || error); // TODO (medium) should use <RestErrorNotification> here, but leads to unexpected webpack error
    }
}

export function* activateAccount({
    newPassword,
    newUsername,
    activationCode,
    userId,
    newTwofaCode,
    newOtpSecret,
    promise,
}) {
    try {
        yield restClient.post(`/auth/activate`, {
            new_password: newPassword,
            new_user_name: newUsername,
            activation_code: activationCode,
            user_id: userId,
            ...(newTwofaCode &&
                newOtpSecret && {
                    new_twofa_code: newTwofaCode,
                    new_otp_secret: newOtpSecret,
                }),
        });

        yield put({
            type: authActions.ACTIVATE_ACCOUNT_SUCCESSFUL,
        });
        yield call(promise.resolve);
    } catch (error) {
        if (isRequestCancelled(error)) {
            return;
        }
        const errorData = error?.response?.data || error;
        yield put({
            type: authActions.ACTIVATE_ACCOUNT_FAILED,
        });
        yield call(promise.reject, errorData);
    }
}

/**
 * Register action to watcher
 */
export default [
    takeLatest(authActions.START_SIGN_IN, signIn),
    takeLatest(authActions.START_SIGN_OUT, signOut),
    takeLatest(authActions.START_ACTIVATE_ACCOUNT, activateAccount),
];
